/*
 * EndpointResponseModel — the live-response store behind <vitesse-endpoint-viewport>,
 * a plain DOM-free class (the same shape as base-ui's data-grid-window.ts) so its
 * promises are pinned under Node.
 *
 * The response pane is LIVE: an op's flattened leaves are addressable values that
 * arrive on the window-follow stream, and the pane projects the current op's
 * leaves as fields. The in-component Map this replaces had three defects:
 *
 *   • It kept every value that ever arrived, for every op ever visited — RAM grew
 *     with time, never with what's on screen.
 *   • Every frame COPIED the whole store to give Lit a new identity.
 *   • Fields were re-sorted alphabetically on every render, so a late-arriving
 *     leaf inserted itself mid-list and shoved the rows under the reader down —
 *     the one thing "nothing moves that the user did not move" forbids.
 *
 * Here: BOUNDED (the current op in full; recently viewed ops as a cushion up to
 * CUSHION entries, oldest op evicted past it — the topic screen's prune-to-window
 * idea, per op instead of per page); IN PLACE (a frame mutates, and reports
 * whether anything RELEVANT — the current op — changed, so the pane re-renders
 * only then); and STABLE (a field gets its position the first time it is seen and
 * keeps it: the first batch for an op is sorted once for a tidy first paint,
 * everything after appends).
 */

import type { EndpointField, FieldKind } from './endpoint-builder';

/** Retained (path → value) entries across ALL ops before the oldest non-current
 *  op is evicted. ~1k rendered scalars is a few hundred KB at most, and lets a
 *  user flip back to a recent op with its response already populated. */
export const CUSHION = 1024;

/** One value frame item, as the window-follow stream delivers it. */
export interface ResponseItem {
  symbolId: string;
  value: unknown;
}

/** Infer the response viewer's scalar kind from a delivered display value. The
 *  value plane hands us rendered scalars, so we classify by shape. */
export function kindOf(v: string): FieldKind {
  if (v === 'null' || v === '') return 'null';
  if (v === 'true' || v === 'false') return 'bool';
  return Number.isFinite(Number(v)) ? 'number' : 'string';
}

interface OpFields {
  /** Field keys in DISPLAY order — append-only after the first batch. */
  keys: string[];
}

export class EndpointResponseModel {
  /** path → display value, every retained op. Handed to the op-list read-only:
   *  the SAME Map identity across frames (nothing is copied), paired with
   *  `version` for change detection. */
  private readonly flat = new Map<string, string>();
  /** op target → its stable field order. Map insertion order doubles as the
   *  LRU: setTarget() re-inserts the current op so it is always newest. */
  private readonly ops = new Map<string, OpFields>();
  private target = '';
  private versionValue = 0;
  private cache: EndpointField[] | null = null;

  /** Read-only view of every retained value (the op-list's `.values`). */
  get values(): ReadonlyMap<string, string> {
    return this.flat;
  }
  /** Bumps whenever something RELEVANT changed (see apply). */
  get version(): number {
    return this.versionValue;
  }
  /** Retained entries — the number CUSHION bounds. */
  get size(): number {
    return this.flat.size;
  }

  /** Make `target` the current op. Its fields (if retained) become the pane's
   *  response immediately; it moves to newest in the LRU so it can't be evicted. */
  setTarget(target: string): void {
    if (target === this.target) return;
    this.target = target;
    const op = this.ops.get(target);
    if (op) {
      this.ops.delete(target);
      this.ops.set(target, op);
    }
    this.cache = null;
    this.versionValue++;
  }

  /** Apply one value frame IN PLACE. Returns true iff the CURRENT op's fields
   *  changed (a new leaf, or a leaf's value) — the only case the pane must
   *  re-render for. Frames for other ops are retained silently (the cushion). */
  apply(items: readonly ResponseItem[]): boolean {
    let relevant = false;
    // Group the frame's NEW keys per op so an op's first batch can be sorted
    // once — later arrivals append in arrival order.
    let fresh: Map<string, string[]> | null = null;
    for (const it of items) {
      const path = it.symbolId;
      const slash = path.indexOf('/');
      if (slash <= 0) continue; // an op-level value, not a response leaf
      const value = String(it.value);
      const opName = path.slice(0, slash);
      const prev = this.flat.get(path);
      if (prev === undefined) {
        this.flat.set(path, value);
        if (!fresh) fresh = new Map();
        let list = fresh.get(opName);
        if (!list) fresh.set(opName, (list = []));
        list.push(path.slice(slash + 1));
        if (opName === this.target) relevant = true;
      } else if (prev !== value) {
        this.flat.set(path, value);
        if (opName === this.target) relevant = true;
      }
    }
    if (fresh) {
      for (const [opName, keys] of fresh) {
        const op = this.ops.get(opName);
        if (op) {
          op.keys.push(...keys);
        } else {
          this.ops.set(opName, { keys: keys.sort() }); // first sight of this op: tidy first paint
        }
      }
      this.evict();
    }
    if (relevant) {
      this.cache = null;
      this.versionValue++;
    }
    return relevant;
  }

  /** The current op's fields in their STABLE display order. Cached until
   *  something relevant changes. Empty when nothing has arrived for it yet —
   *  absent is absent, never a fabricated row. */
  fields(): EndpointField[] {
    if (this.cache) return this.cache;
    const op = this.ops.get(this.target);
    if (!op) return (this.cache = []);
    const prefix = `${this.target}/`;
    const out: EndpointField[] = new Array(op.keys.length);
    for (let i = 0; i < op.keys.length; i++) {
      const key = op.keys[i];
      const value = this.flat.get(prefix + key) ?? '';
      out[i] = { key, kind: kindOf(value), value, live: true };
    }
    return (this.cache = out);
  }

  /** Drop the oldest non-current ops until the store fits the cushion. The
   *  current op is never evicted, however large its response. */
  private evict(): void {
    if (this.flat.size <= CUSHION) return;
    for (const [opName, op] of this.ops) {
      if (this.flat.size <= CUSHION) return;
      if (opName === this.target) continue;
      for (const key of op.keys) this.flat.delete(`${opName}/${key}`);
      this.ops.delete(opName);
    }
  }
}
